import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, humanise, mobile as fmtMobile, num } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Notice } from '../components/ui';

interface Person {
  id: string;
  displayName: string | null;
  mobile: string;
}

interface Summary {
  cards: {
    failedLogins24h: number;
    signIns24h: number;
    activeSessions: number;
    blockedAccounts: number;
    lockedAccounts: number;
    multiDeviceUsers: number;
  };
  repeatedFailures: Array<{ identifier: string; attempts: number }>;
  multiDevice: Array<{ user: Person; sessions: number }>;
  recentlyEnded: Array<{
    id: string;
    identifier: string;
    logoutAt: string | null;
    logoutReason: string | null;
    user: Person | null;
  }>;
  events: Array<{ id: string; action: string; entityId: string | null; createdAt: string; ip: string | null; actor: Person | null }>;
}

const name = (p: Person | null) => (p ? (p.displayName ?? fmtMobile(p.mobile)) : 'system');

function Stat({ label, value, tone, to }: { label: string; value: number; tone?: 'red' | 'amber'; to?: string }) {
  const body = (
    <div className="stat">
      <div>
        <div className="stat__label">{label}</div>
        <div className="stat__value" style={tone && value > 0 ? { color: `var(--${tone === 'red' ? 'danger' : 'amber'})` } : undefined}>
          {num(value)}
        </div>
      </div>
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </Link>
  ) : (
    body
  );
}

export function SecurityPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['security-summary'],
    queryFn: () => api.get<Summary>('/admin/security/summary'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <div className="skeleton" style={{ height: 320 }} />;
  if (isError) return <Empty icon="⚠️" title="Could not load the security overview" body={(error as Error).message} />;
  if (!data) return null;

  const { cards } = data;
  const worrying = cards.failedLogins24h >= 10 || data.repeatedFailures.length > 0;

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      {worrying ? (
        <Notice tone="danger" title="Repeated failed sign-ins in the last 24 hours">
          The same number, email or Login ID has been refused three or more times. It may be somebody mistyping, or
          somebody guessing. The list below says which.
        </Notice>
      ) : (
        <Notice tone="info" title="Nothing unusual in the last 24 hours">
          No identifier has been refused three or more times.
        </Notice>
      )}

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button size="sm" loading={isFetching} onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <div className="grid grid--stats">
        <Stat label="Failed sign-ins, 24 h" value={cards.failedLogins24h} tone="red" to="/login-history?status=FAILED" />
        <Stat label="Successful sign-ins, 24 h" value={cards.signIns24h} to="/login-history?status=SUCCESS" />
        <Stat label="Sessions signed in now" value={cards.activeSessions} to="/login-history?active=true" />
        <Stat label="Blocked accounts" value={cards.blockedAccounts} tone="amber" to="/users?blocked=true" />
        <Stat label="Temporarily locked" value={cards.lockedAccounts} tone="amber" />
        <Stat label="Signed in on several devices" value={cards.multiDeviceUsers} />
      </div>

      <div className="grid grid--2">
        <Card flush>
          <CardHead title="Repeated failures" sub="Refused three or more times in 24 hours" />
          {data.repeatedFailures.length === 0 ? (
            <Empty icon="✓" title="None" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Identifier</th>
                    <th>Attempts</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.repeatedFailures.map((r) => (
                    <tr key={r.identifier}>
                      <td className="cell-mono">{r.identifier}</td>
                      <td className="cell-strong">{r.attempts}</td>
                      <td>
                        <Link to={`/login-history?status=FAILED&search=${encodeURIComponent(r.identifier)}`}>See attempts</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card flush>
          <CardHead title="Several devices at once" sub="Accounts with more than one live session" />
          {data.multiDevice.length === 0 ? (
            <Empty icon="✓" title="None" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.multiDevice.map((m) => (
                    <tr key={m.user.id}>
                      <td>
                        <Link to={`/users/${m.user.id}`}>{name(m.user)}</Link>
                      </td>
                      <td className="cell-strong">{m.sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card flush>
        <CardHead title="Recent security actions" sub="Blocks, locks, forced sign-outs and role changes in the last 7 days" />
        {data.events.length === 0 ? (
          <Empty icon="⌚" title="No security actions this week" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>On</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.id}>
                    <td className="cell-muted">{dateTime(e.createdAt)}</td>
                    <td>{e.actor ? <Link to={`/users/${e.actor.id}`}>{name(e.actor)}</Link> : 'system'}</td>
                    <td>
                      <Badge tone={/UNBLOCK|UNLOCK|GRANT/.test(e.action) ? 'green' : 'red'}>{humanise(e.action)}</Badge>
                    </td>
                    <td className="cell-mono cell-clip">{e.entityId ?? '—'}</td>
                    <td className="cell-mono">{e.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card flush>
        <CardHead title="Sessions ended by the system or an admin" sub="Last 7 days" />
        {data.recentlyEnded.length === 0 ? (
          <Empty icon="✓" title="None" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Ended</th>
                  <th>Account</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {data.recentlyEnded.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-muted">{dateTime(r.logoutAt)}</td>
                    <td>{r.user ? <Link to={`/users/${r.user.id}`}>{name(r.user)}</Link> : r.identifier}</td>
                    <td>{humanise(r.logoutReason ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
